<script setup lang="ts">
import {
  DialogContent,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
} from 'reka-ui'
import { computed, nextTick, toRef, useId, watch } from 'vue'
import { useOverlayStack } from '~/composables/ui/useOverlayStack'

type SourcePopinSize = 'small' | 'medium' | 'large' | 'xlarge'

const props = withDefaults(
  defineProps<{
    open: boolean
    title: string
    description?: string
    size?: SourcePopinSize
    /** Bouton fermer (X). false pour confirmations destructives forcées. */
    hasClear?: boolean
    closeOnBackdrop?: boolean
    closeOnEscape?: boolean
    descriptionId?: string
    /** Classes CSS additionnelles sur le panneau (ex. overlay--sections-manager). */
    contentClass?: string | Record<string, boolean> | Array<string | Record<string, boolean>>
  }>(),
  {
    description: undefined,
    size: 'medium',
    hasClear: true,
    closeOnBackdrop: true,
    closeOnEscape: true,
    descriptionId: undefined,
    contentClass: undefined,
  }
)

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void
  (e: 'close'): void
}>()

const overlayId = useId()
const layer = useOverlayStack(toRef(props, 'open'))
const layerClass = computed(() => `source-popin-layer-${layer.value.depth}`)
const resolvedDescriptionId = computed(
  () => props.descriptionId ?? (props.description || undefined ? 'source-popin-desc' : undefined)
)

function onOpenChange(open: boolean): void {
  if (!open) {
    emit('update:open', false)
    emit('close')
    return
  }
  emit('update:open', true)
}

function isRteLinkPopoverEvent(event: Event): boolean {
  const native = (event as CustomEvent<{ originalEvent?: Event }>).detail?.originalEvent ?? event
  const path = typeof native.composedPath === 'function' ? native.composedPath() : []
  if (path.some((node) => node instanceof Element && node.classList.contains('source-rte-link-pop'))) {
    return true
  }
  const target = native.target
  return target instanceof Element && Boolean(target.closest('.source-rte-link-pop'))
}

function onEscapeKeyDown(event: KeyboardEvent): void {
  if (!props.closeOnEscape) {
    event.preventDefault()
    return
  }
  event.preventDefault()
  onOpenChange(false)
}

function eventTargetsPopper(event: Event): boolean {
  const native = (event as CustomEvent<{ originalEvent?: Event }>).detail?.originalEvent ?? event
  const path = typeof native.composedPath === 'function' ? native.composedPath() : []
  if (path.some((node) => node instanceof Element && Boolean(node.closest('[data-reka-popper-content-wrapper]')))) {
    return true
  }
  const target = native.target
  return target instanceof Element && Boolean(target.closest('[data-reka-popper-content-wrapper]'))
}

function onPointerDownOutside(event: Event): void {
  if (isRteLinkPopoverEvent(event) || eventTargetsPopper(event)) {
    event.preventDefault()
    return
  }
  if (!props.closeOnBackdrop) {
    event.preventDefault()
    return
  }
  event.preventDefault()
  onOpenChange(false)
}

function onFocusOutside(event: Event): void {
  if (isRteLinkPopoverEvent(event) || eventTargetsPopper(event)) {
    event.preventDefault()
  }
}

function onCloseClick(): void {
  onOpenChange(false)
}

function paintOverlayLayer(): void {
  if (!props.open || typeof document === 'undefined') return
  const scrim = document.querySelector<HTMLElement>(
    `[data-overlay-id="${overlayId}"][data-overlay-part="scrim"]`,
  )
  const content = document.querySelector<HTMLElement>(
    `[data-overlay-id="${overlayId}"][data-overlay-part="content"]`,
  )
  scrim?.style.setProperty('z-index', String(layer.value.scrim), 'important')
  content?.style.setProperty('z-index', String(layer.value.content), 'important')
}

watch(
  () => [props.open, layer.value.scrim] as const,
  () => {
    void nextTick(paintOverlayLayer)
  },
  { flush: 'post' },
)
</script>

<template>
  <DialogRoot :open="open" @update:open="onOpenChange">
    <DialogPortal>
      <DialogOverlay
        class="source-popin__scrim"
        :class="layerClass"
        :data-overlay-id="overlayId"
        data-overlay-part="scrim"
      />
      <DialogContent
        class="source-popin"
        :class="[`source-popin--${size}`, layerClass, contentClass]"
        :data-overlay-id="overlayId"
        data-overlay-part="content"
        :aria-describedby="resolvedDescriptionId"
        @escape-key-down="onEscapeKeyDown"
        @pointer-down-outside="onPointerDownOutside"
        @focus-outside="onFocusOutside"
      >
        <!--
          Inner clip : overflow/radius ici pour ne pas couper l’ombre
          elevation Source sur .source-popin (overflow:hidden clippe box-shadow).
        -->
        <div class="source-popin__clip">
          <header class="source-popin__header">
            <div class="source-popin__header-text">
              <DialogTitle class="source-popin__title">{{ title }}</DialogTitle>
              <DialogDescription
                v-if="description || $slots.description"
                :id="resolvedDescriptionId"
                class="source-popin__description"
              >
                <slot name="description">{{ description }}</slot>
              </DialogDescription>
            </div>
            <div class="source-popin__header-end">
              <slot name="header-actions" />
              <button
                v-if="hasClear"
                type="button"
                class="source-popin__clear"
                aria-label="Fermer"
                @click="onCloseClick"
              >
                <span class="material-symbols-rounded" aria-hidden="true">close</span>
              </button>
            </div>
          </header>

          <div v-if="$slots.default" class="source-popin__body">
            <slot />
          </div>

          <footer v-if="$slots.actions" class="source-popin__actions">
            <slot name="actions" />
          </footer>
        </div>
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>

<style scoped lang="scss">
@use "~/assets/scss/partials/elpdl/colors" as c;
@use "~/assets/scss/partials/elpdl/spacing" as *;
@use "~/assets/scss/partials/elpdl/elevation" as elev;
@use "~/assets/scss/partials/elpdl/z-index" as z;

.source-popin__scrim {
  position: fixed;
  inset: 0;
  background: var(--color-backdrop-strong);
}

.source-popin {
  position: fixed;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  gap: 0;
  max-height: min(85vh, 720px);
  width: min(35rem, calc(100vw - 2 * #{$spacing-l}));
  min-width: min(28rem, calc(100vw - 2 * #{$spacing-l}));
  min-height: 16rem;
  padding: 0;
  /* Pas d’overflow:hidden ici : ça clippe l’ombre Source (box-shadow). */
  overflow: visible;
  background: transparent;
  border: none;
  border-radius: 4px;
  /* Pop-in au-dessus des panels flottants (200) - échelle Source */
  @include elev.elevation-300;
  box-sizing: border-box;
}

.source-popin__clip {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  max-height: inherit;
  width: 100%;
  overflow: hidden;
  border-radius: 4px;
  background: c.$enedis-neutral-10;
}

.source-popin--xlarge .source-popin__clip {
  height: 100%;
}

.source-popin--small {
  width: min(24rem, calc(100vw - 2 * #{$spacing-l}));
  min-width: min(22rem, calc(100vw - 2 * #{$spacing-l}));
  min-height: 12rem;
}

.source-popin--medium {
  width: min(35rem, calc(100vw - 2 * #{$spacing-l}));
  min-height: 16rem;
}

.source-popin--large {
  width: min(45rem, calc(100vw - 2 * #{$spacing-l}));
  min-width: min(36rem, calc(100vw - 2 * #{$spacing-l}));
  min-height: 20rem;
  max-height: min(90vh, 800px);
}

@for $depth from 0 through z.$z-modal-max-depth {
  .source-popin-layer-#{$depth}.source-popin__scrim {
    z-index: #{z.$z-modal + $depth * z.$z-modal-step} !important;
  }

  .source-popin-layer-#{$depth}.source-popin {
    z-index: #{z.$z-modal + $depth * z.$z-modal-step + 1} !important;
  }
}

.source-popin.source-popin--no-scroll {
  max-height: none;
  height: auto;
}

.source-popin.source-popin--no-scroll .source-popin__body {
  overflow: hidden;
  flex: 0 0 auto;
  min-height: unset;
}

.source-popin--xlarge {
  top: $spacing-xl;
  bottom: $spacing-xl;
  left: 50%;
  transform: translateX(-50%);
  width: min(60rem, calc(100vw - 2 * #{$spacing-m}));
  max-height: none;
  height: auto;
}

:deep(.overlay--sections-manager),
.source-popin.overlay--sections-manager {
  width: min(70rem, 96vw);
}

.source-popin__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: $spacing-m;
  flex-shrink: 0;
  padding: $spacing-l $spacing-xl;
  border-bottom: 1px solid c.$enedis-neutral-60;
  background: c.$enedis-neutral-10;
}

.source-popin__header-end {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: $spacing-xs;
  flex-shrink: 0;
}

.source-popin__header-text {
  display: flex;
  flex-direction: column;
  gap: $spacing-3xs;
  min-width: 0;
  flex: 1;
}

.source-popin__title {
  margin: 0;
  font-family: Enedis, sans-serif;
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.3;
  color: c.$enedis-neutral-900;
}

.source-popin__description {
  margin: 0;
  font-family: Public Sans, sans-serif;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.45;
  color: c.$enedis-neutral-500;
}

.source-popin__clear {
  all: unset;
  box-sizing: border-box;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 4px;
  color: c.$enedis-neutral-500;
  cursor: pointer;

  &:hover {
    background: var(--color-inverse-hover);
    color: c.$enedis-neutral-800;
  }

  &:focus-visible {
    outline: 2px solid c.$enedis-primary-500;
    outline-offset: 2px;
  }

  .material-symbols-rounded {
    font-size: 1.5rem;
  }
}

.source-popin__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: $spacing-l $spacing-xl;
  display: flex;
  flex-direction: column;
  gap: $spacing-m;
  font-family: Public Sans, sans-serif;
  color: c.$enedis-neutral-700;
}

.source-popin__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
  gap: $spacing-s;
  flex-shrink: 0;
  padding: $spacing-m $spacing-xl $spacing-l;
  border-top: 1px solid c.$enedis-neutral-60;
  background: c.$enedis-neutral-10;
}

/* Hauteur bornée seulement pour le gestionnaire de sections : le pied reste visible. */
.source-popin.overlay--sections-manager:not(.source-popin--xlarge) {
  height: min(90vh, 800px);
  max-height: min(90vh, 800px);
}

.source-popin.overlay--sections-manager .source-popin__clip {
  height: 100%;
  max-height: 100%;
}

/* Overlay : le formulaire gère le scroll (champs) et son propre footer figé. */
.source-popin.overlay-panel .source-popin__body {
  overflow: hidden;
  padding-bottom: 0;
  gap: 0;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
}

.source-popin.overlay-panel.overlay--training-center .source-popin__body {
  overflow-x: hidden;
  overflow-y: auto;
}

.source-popin.overlay-panel .source-popin__body > * {
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
}
</style>
